import {byId} from './dom'

/**
 * The "this content makes sound" dialog, answered before the camera starts.
 *
 * Same split as `createHud` and `createCaptureUi`: markup and copy live in
 * `index.html`, this file only drives it. Either button starts the experience -
 * the choice is nothing more than the initial value of the shared mute state.
 *
 * The tap is deliberately NOT used to unlock the AudioContext. None exists at
 * this point (the bank builds one only after the manifest names a sound URL,
 * which happens after the camera starts), and the tap's transient activation
 * does not survive the camera permission prompt sitting in between. The
 * placement tap and the mute toggle both pass through the window-level unlock
 * listeners later, and that is the path that actually works.
 */

/** Mirrors the ids in `index.html`. */
const ELEMENT_IDS = {
  dialog: 'sound-consent',
  start: 'sound-consent-start',
  muted: 'sound-consent-muted',
} as const

/** Shows the dialog and resolves with the choice: `true` means sound on. */
export const askSoundConsent = (): Promise<boolean> => {
  const dialog = byId(ELEMENT_IDS.dialog, HTMLDivElement)
  const startButton = byId(ELEMENT_IDS.start, HTMLButtonElement)
  const mutedButton = byId(ELEMENT_IDS.muted, HTMLButtonElement)

  return new Promise((resolve) => {
    const abort = new AbortController()
    const choose = (soundOn: boolean): void => {
      abort.abort()
      dialog.hidden = true
      resolve(soundOn)
    }
    startButton.addEventListener('click', () => choose(true), {signal: abort.signal})
    mutedButton.addEventListener('click', () => choose(false), {signal: abort.signal})
    dialog.hidden = false
    // role=dialog does not move focus on its own.
    startButton.focus()
  })
}
